import { HttpClient } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { DataStateChangeEventArgs } from '@syncfusion/ej2-angular-grids';
import { UploadingEventArgs } from '@syncfusion/ej2-angular-inputs';
import { DialogUtility } from '@syncfusion/ej2-angular-popups';
import { EmitType } from '@syncfusion/ej2-base';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError, Subject, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpHelper } from '../helpers/http-helper';
import { IApiResult } from '../models/api-result';
import { IApiTypedResult, IBusinessOperationQualifier, ILLGenericKeys, ITermByIndustryUsageAndCategory, Order, OrderUser, SplFileStorageResult, User } from '../models/index';
import { AuthenticationService } from './authentication.service';
import { AuthorizationService } from './authorization.service';
import { SettingsService } from './settings.service';
import { StorageManagerService } from './storage-manager.service';


@Injectable()
export class OrderService extends Subject<DataStateChangeEventArgs> {

    // Helper class
    httpHelper: HttpHelper;

    //
    // Public constants
    //

    // Order types
    public readonly SERVICE_CATEGORY_FULL_CONVERSION: string = 'Full Conversion';
    public readonly SERVICE_CATEGORY_PARTIAL_CONVERSION: string = 'Partial Conversion';
    public readonly SERVICE_CATEGORY_ESG: string = 'ESG (FDA Submission)';

    // Service groups
    public readonly SERVICE_GROUP_ESG: string = 'ESG Submission';
    public readonly SERVICE_GROUP_ER: string = 'Establishment Registration and Certification';
    public readonly SERVICE_GROUP_REPORT: string = 'Reports';
    public readonly SERVICE_GROUP_PUBLISHING: string = 'Composition Publishing';
    public readonly SERVICE_GROUP_REMS: string = 'Risk Evaluation & Mitigation Strategy';
    public readonly SERVICE_GROUP_LABEL_CONVERSION: string = 'Label Conversion';

    // Delivery priorities
    public readonly DELIVERY_PRIORITY_REGULAR: string = 'Regular';
    public readonly DELIVERY_PRIORITY_EXPEDITED: string = 'Expedited';
    public readonly DELIVERY_PRIORITY_EMERGENCY: string = 'Emergency';

    // Association type codes for reference data
    public readonly TERM_ASSOCIATION_TYPE_SERVICE_CATEGORY_TO_SERVICE_TYPE = 4;
    public readonly TERM_ASSOCIATION_TYPE_SERVICE_TYPE_TO_DELIVERY_PRIORITY = 5;
    public readonly TERM_ASSOCIATION_TYPE_COUNTRY_TO_SERVICE_TYPE = 10;
    public readonly TERM_ASSOCIATION_TYPE_COUNTRY_TO_SERVICE_GROUP = 11;

    // Usage codes for reference data
    public readonly USAGE_CODE_SSDP = 3;

    // Industry id's
    public readonly INDUSTRY = 1;

    // Term categories
    public readonly TERM_CATEGORY_SERVICE_CATEGORY = 47;
    public readonly TERM_CATEGORY_SERVICE_TYPE = 48;
    public readonly TERM_CATEGORY_DELIVERY_PRIORITY = 49;
    public readonly TERM_CATEGORY_ORDER_STATUS = 50;
    

    // Order user types
    public readonly ORDER_USER_TYPE_ORDER_OWNER = 1;
    public readonly ORDER_USER_TYPE_ORDER_RECIPIENT = 2;
    public readonly ORDER_USER_TYPE_ORDER_INITIATOR = 3;
    public readonly ORDER_USER_TYPE_ORDER_SUBMITTER = 4;

    // Upload file types
    public readonly UPLOAD_FILE_TYPE_SOURCE_DOC = 'SourceDoc';
    public readonly UPLOAD_FILE_TYPE_DELIVERY = 'Delivery';

    // Order status
    public readonly ORDER_STATUS_DRAFT = 'Draft';
    public readonly ORDER_STATUS_PENDING = 'Pending';
    public readonly ORDER_STATUS_IN_PROCESS = 'In Process';
    public readonly ORDER_STATUS_ORDER_DELIVERED_AWAITING_APPROVAL = 'Order Delivered - Awaiting Approval';
    public readonly ORDER_STATUS_ORDER_COMPLETE = 'Order Complete';
    public readonly ORDER_STATUS_CANCELED = 'Canceled';
    public readonly ORDER_STATUS_PENDING_CANCELLATION_REQUEST = 'Pending - Cancellation Request';

    // Page status
    public readonly PAGE_STATE_LOADING = 'Loading';
    public readonly PAGE_STATE_READY = 'Ready';
    public readonly PAGE_STATE_SAVING = 'Saving';

    // Permissions
    public readonly PERMISSION_CAN_READ_ORDER = 'SSDP.CanReadOrder';
    public readonly PERMISSION_CAN_WRITE_ORDER = 'SSDP.CanWriteOrder';
    public readonly PERMISSION_CAN_DELETE_DRAFT_ORDER = 'SSDP.CanDeleteDraftOrder';
    public readonly PERMISSION_CAN_CANCEL_ORDER = 'SSDP.CanCancelOrder';
    public readonly PERMISSION_ORDER_ADMIN = 'SSDP.CanUpdateOrder';



    // NOTE: file upload validation settings pulled from SettingsService and appSettings.json

    // These letters not allowed in file name for upload
    invalidFileNameCharsArr: string[];
    // Invalid characters as display string
    invalidFileNameCharsStr: string;
    // Invalid names for files
    invalidFileNameRegex: RegExp;
    // Valid file types as array
    validFileExtensionsArr: string[];
    // Valid file types as comma delimited string
    validFileExtensionsStr: string;
    // Max allowable file size in bytes
    fileMaxSize: number;
    // Min allowable file size in bytes
    fileMinSize: number;
    // Max allowable file name length
    fileNameMaxLength: number;

    constructor(private http: HttpClient,
        private settingsService: SettingsService,
        private authenticationService: AuthenticationService,
        private authorizationService: AuthorizationService,
        private storageManagerService: StorageManagerService,
        private toastr: ToastrService,
        private router: Router
    ) {
        super();

        // Define an instance of the http helper class that will be used to create httpHeader for api calls
        this.httpHelper = new HttpHelper(http, authenticationService, storageManagerService);

        // Pull in file upload validation settings from appsettings.json
        let settings = this.settingsService.getSettings();
        this.invalidFileNameCharsStr = settings.InvalidFileNameChars;
        this.invalidFileNameCharsArr = this.invalidFileNameCharsStr.split(',');
        this.invalidFileNameRegex = new RegExp(settings.InvalidFileNameRegex);
        this.validFileExtensionsStr = settings.ValidFileExtensions.toLocaleLowerCase();
        this.validFileExtensionsArr = this.validFileExtensionsStr.split(',');
        this.fileMaxSize = settings.FileMaxSize;
        this.fileMinSize = settings.FileMinSize;
        this.fileNameMaxLength = settings.FileNameMaxLength;

    }

    // Get order data by id
    getOrderByOrderRevisionId(orderRevisionId: number): Observable<IApiTypedResult<Order>> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.ApiGatewayUrl;
        const action = `ssdpds/v1/Orders/${orderRevisionId}`;

        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
            .pipe(map(response => {
                return this.extractTypedBody<IApiTypedResult<Order>>(response as Response);
            }),
            catchError(this.handleErrors));
    }

    // Get file storage info about SPL XML associated with llRevisionId for an order
    getFileStorageSplXml(llRevisionId: number): Observable<IApiTypedResult<SplFileStorageResult>> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.ApiGatewayUrl;
        const action = `ssdpds/v1/FileStorage/SplXml/${llRevisionId}?formType=LL`;

        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
            .pipe(map(response => {
                return this.extractTypedBody<IApiTypedResult<SplFileStorageResult>>(response as Response);
            }),
            catchError(this.handleErrors));
    }

    // Save order.  Always creates a new OrderRevision record.
    saveOrder(order: Order, modifiedByUser: string): Observable<IApiTypedResult<Order>> {
        let headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.ApiGatewayUrl;
        const action = `ssdpds/v1/Orders`;

        headers = headers.set('modifiedByUser', modifiedByUser);
        // Special case, C# also needs current customer name as a header
        const sessionCurrentCustomer = this.storageManagerService.read('currentCustomer');
        const currentCustomer = typeof sessionCurrentCustomer === 'object' ? sessionCurrentCustomer : JSON.parse(sessionCurrentCustomer);
        headers = headers.set('customerName', currentCustomer.customerName);

        // Special case-- isDraftMode should be 1 if orderStatus=draft, else 0
        order.isDraftMode = (order.orderStatus === this.ORDER_STATUS_DRAFT ? true : false);

        return this.http.post(`${apiUrl}/${action}?isDraftMode=${order.isDraftMode}`, order, { headers: headers })
          .pipe(map(response => this.extractTypedBody<IApiTypedResult<Order>>(response as Response)),
            catchError(this.handleErrors));
    }

    // Clone order.  Makes a copy, ands a comment to the new one saying it was a copy of the old one.
    cloneOrder(order: Order, modifiedByUser: string): Observable<IApiResult> {
        let headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.ApiGatewayUrl;
        const action = `ssdpds/v1/Orders/OrderClone`;
        headers = headers.set('modifiedByUser', modifiedByUser);
        return this.http.post(`${apiUrl}/${action}`, order, { headers: headers })
          .pipe(map(response => this.extractTypedBody<IApiTypedResult<Order>>(response as Response)),
            catchError(this.handleErrors));
    }

    // Get list of service types by service category
    getServiceCategoryToServiceTypeAssoc(): Observable<IBusinessOperationQualifier[]> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.CommonApiGatewayUrl;
        const action = `rds/v1/Terms/TermsAssociation?associationTypeCode=${this.TERM_ASSOCIATION_TYPE_SERVICE_CATEGORY_TO_SERVICE_TYPE}&usageCode=${this.USAGE_CODE_SSDP}`;
        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
          .pipe(map(response => this.extractTypedBody<IBusinessOperationQualifier[]>(response as Response)),
            catchError(this.handleErrors));
    }

    // Get list of delivery priorities by service type
    getServiceTypeToDevlieryPriorityAssoc(): Observable<IBusinessOperationQualifier[]> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.CommonApiGatewayUrl;
        const action = `rds/v1/Terms/TermsAssociation?associationTypeCode=${this.TERM_ASSOCIATION_TYPE_SERVICE_TYPE_TO_DELIVERY_PRIORITY}&usageCode=${this.USAGE_CODE_SSDP}`;
        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
          .pipe(map(response => this.extractTypedBody<IBusinessOperationQualifier[]>(response as Response)),
            catchError(this.handleErrors));
    }

    // Get a list of all employees in a customer
    getEmployees(customerGuid: string): Observable<User[]> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.CommonApiGatewayUrl;
        const action = `ids/v1/User/UsersByCustomerGuid?customerGuid=${customerGuid}`;
        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
          .pipe(map(response => this.extractTypedBodyData<User[]>(response as Response)),
            catchError(this.handleErrors));
    }

    // Download a saved file
    downloadFile(filePath: string, fileName: string) {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.ApiGatewayUrl;
        const action = `ssdpds/v1/FileStorage/DownloadFile?filePath=${filePath}&fileName=${fileName}`;
        return this.http.get(`${apiUrl}/${action}`, { headers: headers, responseType: 'blob' })
          .pipe(map(response => response),
            catchError(this.handleErrors));
    }

    // After file is downloaded, trigger to save as browser window
    postDownloadFile(response: any, fileName: string) {
        const URL = window.URL || (<any>window).webkitURL;
        const downloadUrl = URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        link.click();
    }

    // Convert an order's OrderUser list to user guid array based on OrderUserTypeId using allEmployeesInCompany
    // to find first/last names.
    getUserGuidsFromOrderUsers(allEmployeesInCompany: User[], orderOwners: OrderUser[], orderUserTypeId: number): string[] {
        let userGuids = [];
        if (allEmployeesInCompany && orderOwners) {
            // Get the user logins we want
            const userLoginsWeWant: string[] = orderOwners
                .filter(x => x.orderUserTypeId === orderUserTypeId)
                .map(x => x.userLogin.toLowerCase());
            // Translate to user guids
            userGuids = allEmployeesInCompany
                .filter(x => userLoginsWeWant.indexOf(x.loginName.toLowerCase()) > -1)
                .map(x => x.userGuid);
        }
        return userGuids;
    }

    // Convert an order's OrderUser list to a comma delimited list of names based on OrderUserTypeId using allEmployeesInCompany
    // to find first/last names.
    getUserNamesFromOrderUsers(allEmployeesInCompany: User[], orderUsers: OrderUser[], orderUserTypeId: number): string {
        let userNames = '';
        if (allEmployeesInCompany && orderUsers) {
            // Get the user logins we want
            const userLoginsWeWant: string[] = orderUsers
                .filter(x => x.orderUserTypeId === orderUserTypeId)
                .map(x => x.userLogin.toLowerCase());
            // Translate to user names joined with commas
            userNames = allEmployeesInCompany
                .filter(x => userLoginsWeWant.indexOf(x.loginName.toLowerCase()) > -1)
                .map(x => x.firstName + ' ' + x.lastName)
                .join(', ');
        }
        return userNames;
    }

    // Given a list of all employees in company, the subset of userGuids we want, and orderUserTypeId,
    // return an array of matching OrderUser objects
    userGuidsToOrderUsers(allEmployeesInCompany: User[], userGuids: string[], orderUserTypeId: number): OrderUser[] {
        const orderUsers: OrderUser[] = [];
        for (const emp of allEmployeesInCompany) {
            if (userGuids.indexOf(emp.userGuid) > -1) {
                orderUsers.push(new class implements OrderUser {
                    orderUserTypeId = orderUserTypeId;
                    userLogin = emp.loginName;
                    userEmail = emp.emailAddress;
                    userName = emp.firstName + ' ' + emp.lastName;
                    userGuid = emp.userGuid;
                });
            }
        }
        return orderUsers;
    }

   // ------------------------------------------------------------
    // Due to Migration, if order owner or order email recipient contains a record that is not present
    // add it to its corresponding lists
    // ------------------------------------------------------------
  GetInactiveOrderUsers(allEmployeesInCompany: User[], orderUsers: OrderUser[], orderUserTypeId: number): any[] {
    const inactiveUsers: any[] = [];
      if (orderUsers) {
        orderUsers.forEach(emp => {
              const matchedUser = allEmployeesInCompany.filter(x => x.emailAddress.toUpperCase() === emp.userEmail.toUpperCase() || x.loginName.toUpperCase() === emp.userLogin.toUpperCase());
              if (matchedUser && matchedUser.length > 0) {
                  emp.userGuid = matchedUser[0].userGuid;
                  emp.userLogin = matchedUser[0].loginName.toLowerCase();
                  emp.userEmail = matchedUser[0].emailAddress.toLowerCase();
              }
              if (!allEmployeesInCompany.some(x => x.emailAddress.toUpperCase() === emp.userEmail.toUpperCase() && x.loginName.toUpperCase() === emp.userLogin.toUpperCase())) {
                  if (emp.orderUserTypeId === orderUserTypeId) {
                      inactiveUsers.push({
                          name: emp.userName ? emp.userName : emp.userLogin
                      });
                  }
              }
          });
    }
    return inactiveUsers;
  }

    getFileUploadUrl(): string {
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.ApiGatewayUrl;
        const action = `ssdpds/v1/Orders/UploadFile`;
        return `${apiUrl}/${action}`;
    }

    // Convert any non-number or zero to null
    // because I hate checking for undefined and zero and stuff.
    getNumberOrNull(myNum: number): number {
        if (myNum === null || myNum === undefined || myNum === 0) {
            return null;
        } else {
            return myNum;
        }
    }

    // Get dashboard data inside a APIResult
    getOrderTrackerData(dateRangeLimit: string): Observable<IApiTypedResult<any[]>> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.ApiGatewayUrl;
        const action = `ssdpds/v1/Orders/OrderDashboard?dateRangeLimit=${dateRangeLimit}`;
        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
          .pipe(map(response => this.extractTypedBody<IApiTypedResult<any[]>>(response as Response)),
            catchError(this.handleErrors));
    }

    // Get dashboard data inside a APIResult
    getBaselineSplData(llRevisionId: number): Observable<IApiTypedResult<any[]>> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.ApiGatewayUrl;
        const action = `ssdpds/v1/Orders/BaselineSpl/${llRevisionId}`;
        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
          .pipe(map(response => this.extractTypedBody<IApiTypedResult<any[]>>(response as Response)),
            catchError(this.handleErrors));
    }

    // Extract body of response object or throw exception if bad status
    // Use generic type for better code syntax
    private extractTypedBody<T>(response: Response): T {
        if (response.status < 200 || response.status >= 300) {
            throw new Error('Bad response status: ' + response.status);
        }
        let body: T;
        if (response) {
            body = (response as any) as T;
        } else {
            body = {} as T;
        }
        return body;
    }

    // Extract data attribute of body of response object or throw exception if bad status
    // Use generic type for better code syntax
    private extractTypedBodyData<T>(response: Response): T {
        if (response.status < 200 || response.status >= 300) {
            throw new Error('Bad response status: ' + response.status);
        }
        let body: T;
        if (response && (response as any).data) {
            body = ((response as any).data as any) as T;
        } else {
            body = {} as T;
        }
        return body;
    }

    // Get the current customer guid from browser storage
    public getCustomerGuid(): string {
        let customerGuid = null;
        const currentCustomerStr: string = this.storageManagerService.read('currentCustomer');
        if (currentCustomerStr) {
            const currentCustomer = typeof currentCustomerStr === 'object' ? currentCustomerStr : JSON.parse(currentCustomerStr);
            if (currentCustomer) {
                customerGuid = currentCustomer.customerGuid;
            }
        }
        return customerGuid;
    }

    // Return true if supplied Angular FormGroup has any dirty fields
    // (fields that have been modified by the user and not saved)
    public isAngularFormDirty(angularForm: UntypedFormGroup): boolean {
        let formDirty = false;
        for (const field in angularForm.controls) {
            const fieldControl = angularForm.get(field);
            if (fieldControl.touched && fieldControl.dirty) {
                formDirty = true;
                break;
            }
        }
        // Sometimes we mark the entire form as dirty
        if (angularForm.touched && angularForm.dirty) {
            formDirty = true;
        }

        return formDirty;
    }

    public getOrderStatuses(): Observable<ITermByIndustryUsageAndCategory[]> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.CommonApiGatewayUrl;
        const action = `rds/v1/Terms/industry?industryId=${this.INDUSTRY}&termusageId=${this.USAGE_CODE_SSDP}&categoryId=${this.TERM_CATEGORY_ORDER_STATUS}`;
        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
          .pipe(map(response => this.extractTypedBody<ITermByIndustryUsageAndCategory[]>(response as Response)),
            catchError(this.handleErrors));
    }

    public getServiceTypes(): Observable<ILLGenericKeys[]> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.CommonApiGatewayUrl;
        const action = `rds/v1/Terms/industry?industryId=${this.INDUSTRY}&termusageId=${this.USAGE_CODE_SSDP}&categoryId=${this.TERM_CATEGORY_SERVICE_TYPE}`;
        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
          .pipe(map(response => this.extractTypedBody<ILLGenericKeys[]>(response as Response)),
            catchError(this.handleErrors));
    }

    public getServiceCategories(): Observable<ILLGenericKeys[]> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.CommonApiGatewayUrl;
        const action = `rds/v1/Terms/industry?industryId=${this.INDUSTRY}&termusageId=${this.USAGE_CODE_SSDP}&categoryId=${this.TERM_CATEGORY_SERVICE_CATEGORY}`;
        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
          .pipe(map(response => this.extractTypedBody<ILLGenericKeys[]>(response as Response)),
            catchError(this.handleErrors));
    }

    public getDeliveryPriorities(): Observable<ILLGenericKeys[]> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.CommonApiGatewayUrl;
        const action = `rds/v1/Terms/industry?industryId=${this.INDUSTRY}&termusageId=${this.USAGE_CODE_SSDP}&categoryId=${this.TERM_CATEGORY_DELIVERY_PRIORITY}`;
        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
          .pipe(map(response => this.extractTypedBody<ILLGenericKeys[]>(response as Response)),
            catchError(this.handleErrors));
    }

    public getCountryServiceTypes(): Observable<IBusinessOperationQualifier[]> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.CommonApiGatewayUrl;
        const action = `rds/v1/Terms/TermsAssociation?associationTypeCode=${this.TERM_ASSOCIATION_TYPE_COUNTRY_TO_SERVICE_TYPE}&usageCode=${this.USAGE_CODE_SSDP}`;
        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
          .pipe(map(response => this.extractTypedBody<IBusinessOperationQualifier[]>(response as Response)),
            catchError(this.handleErrors));
    }

    public getCountryServiceGroups(): Observable<IBusinessOperationQualifier[]> {
        const headers = this.httpHelper.createHttpHeaders();
        const settings = this.settingsService.getSettings();
        const apiUrl = settings.CommonApiGatewayUrl;
        const action = `rds/v1/Terms/TermsAssociation?associationTypeCode=${this.TERM_ASSOCIATION_TYPE_COUNTRY_TO_SERVICE_GROUP}&usageCode=${this.USAGE_CODE_SSDP}`;
        return this.http.get(`${apiUrl}/${action}`, { headers: headers })
          .pipe(map(response => this.extractTypedBody<IBusinessOperationQualifier[]>(response as Response)),
            catchError(this.handleErrors));
    }

    // Show a SyncFusion dialog alert box.
    alert(title: string, message: string, callback: Function = null): void {

        // To keep it simple, if no callback, make a dummy one
        if (!callback) {
            callback = () => { ; };
        }

        let callbackAsEmission: EmitType<object> = () => {
            callback();
        }

        let clickCloseButton = (): void => {
            // I can't figure out how to close the dialog from DialogUtility
            // programmatically except by finding the HTML button with the "x" for closing,
            // and forcing a click.  Brian W 7/7/20
            //
            // Also note that the OK button doesn't do the callback, it's when the window
            // is closed that the callback is kicked off.  Otherwise if you close the box
            // with the little "X" button or escape key it won't fire.
            let closeButton = document.getElementsByClassName('e-dlg-closeicon-btn');
            if (closeButton) {
                (closeButton[0] as any).click();
            } else {
                alert('no close button');
            }
        }

        DialogUtility.alert({
            title: title,
            content: message,
            okButton: { text: 'OK', click: clickCloseButton.bind(this) },
            showCloseIcon: true,
            closeOnEscape: true,
            close: callbackAsEmission,
            position: { X: 'center', Y: 'center' },
            animationSettings: { effect: 'Zoom' },
            cssClass: 'alertBox',
            isModal: true
        });
    }

    // Error handling
    private handleErrors(error: any): Observable<any> {
      const GENERIC_ERROR_MSG = 'It looks like something went wrong. Try reloading the page or contacting support if this issue continues.';
      const bDisplayDetailMsg = error && error.error && error.error.isBusinessErrorMessage ? true : isDevMode() ? true : false;
      error.error = error && error.error && error.error.message && bDisplayDetailMsg? error.error.message : GENERIC_ERROR_MSG;

      return throwError(error.error);
    }

    // This code is repeated in multiple steps, consolidate here.
    requestOrderCancellation(canRequestCancellation: boolean, order: Order, modifiedByUser: string, nextPageUrl: string, requestFromOrderTracker: boolean) {
        // Double check perms
        if (canRequestCancellation) {
            // Perms are good, proceed.
          // Only change order status, nothing else, don't worry about dirty flags.
            order.beforeOrderStatus = order.orderStatus
            order.orderStatus = this.ORDER_STATUS_PENDING_CANCELLATION_REQUEST;
            this.saveOrder(order, modifiedByUser).subscribe((response) => {
                // Force response to "any" to make Visual Studio happy
                const responseAsAny: any = response as any;
                if (responseAsAny.isSuccess) {
                  this.alert('Order Cancellation Request', 'Order cancellation request has been received.', () => {
                    let url = '';
                    if (requestFromOrderTracker) {
                      url = nextPageUrl;
                    }
                    else {
                      url = `${nextPageUrl}/${responseAsAny.data.orderRevisionId}`;
                    }
                        this.router.navigate([url]).then(() => {
                            window.location.reload();
                        });
                    });
                } else {
                    this.handleOrderError(this, responseAsAny, 'Order save failed', true);
                }
            },
                error => this.handleOrderError(this, error, 'Order save failed', true)
            );
        } else {
            // This shouldn't really happen, but just in case it does ...
            this.toastr.error('Invalid permissions to request order cancellation.');
            return;
        }
    }

    // Upload file from ejs file upload control to an order.
    public uploadOrderDoc(args: UploadingEventArgs,
        orderId: number,
        orderRevisionId: number,
        component: any,
        reactiveForm: UntypedFormGroup,
        onlyAllowZipFiles: boolean,
        uploadFileDocType: string,
        ingestFile: boolean = false) {

        // Add all the extra headers and form fields we need to do file upload, and do validation.
        if (args) {
            const authData = this.authenticationService.getAuthData();
            args.currentRequest.setRequestHeader('authorization', this.authenticationService.getBearerToken());
            args.currentRequest.setRequestHeader('customerGuid', this.getCustomerGuid());
            args.currentRequest.setRequestHeader('modifiedByUser', this.storageManagerService.read('userData').name);
            args.currentRequest.setRequestHeader('applicationVariantId', authData.applicationVariantId ? authData.applicationVariantId.toString() : '');

            args.customFormData = [
                { 'uploadFileType': uploadFileDocType },
                { 'orderId': '' + orderId },
                { 'orderRevisionId': '' + orderRevisionId },
                { 'ingestFile': '' + ingestFile },
            ];

            const fileData = args.fileData;

            const sizeInBytes: number = fileData.size;
            const fileType: string = fileData.type;
            let fileName: string = fileData.name;

            // Check file size
            if (fileData.size > this.fileMaxSize || fileData.size < this.fileMinSize) {
                args.cancel = true;
                reactiveForm.markAsDirty();
                component.pageState = this.PAGE_STATE_READY;
                this.alert('File Upload Validation',
                    `File size should not be smaller than ${this.fileMinSize} and larger than ${this.fileMaxSize} bytes`);
                return;
            }

            // Check file name against special characters
            fileName = fileName.substring(0, fileName.indexOf('.' + fileType));
            this.invalidFileNameCharsArr.forEach(char => {
                if (fileName.indexOf(char) > -1) {
                    args.cancel = true;
                    reactiveForm.markAsDirty();
                    component.pageState = this.PAGE_STATE_READY;
                    this.alert('File Upload Validation',
                        `Special Characters ${this.invalidFileNameCharsStr} are not allowed in the file name`);
                    return;
                }
            });

            // Check file name agains restricted words
            const matchesRestrictedWord = this.invalidFileNameRegex.test(fileName.toUpperCase());
            if (matchesRestrictedWord) {
                args.cancel = true;
                reactiveForm.markAsDirty();
                component.pageState = this.PAGE_STATE_READY;
                this.alert('File Upload Validation', 'File name contains restricted words.');
                return;
            }

            let localValidFileExtensionsArr = this.validFileExtensionsArr;
            if (onlyAllowZipFiles) {
                localValidFileExtensionsArr = ['.zip'];
            }

            // Check file's type
            if (!localValidFileExtensionsArr.some(x => x === '.' + fileType.toLowerCase())) {
                args.cancel = true;
                reactiveForm.markAsDirty();
                component.pageState = this.PAGE_STATE_READY;
                this.alert('File Upload Validation',
                    `File type ${fileType} is not supported`);
                return;
            }

            // Check file name length
            if (fileName.length > this.fileNameMaxLength) {
                args.cancel = true;
                reactiveForm.markAsDirty();
                component.pageState = this.PAGE_STATE_READY;
                this.alert('File Upload Validation',
                    `File name should not exceed ${this.fileNameMaxLength} characters`);
                return;
            }
        }
    }

    // When dealing with observables that should return Observable<IApiTypedResult<Order>>,
    // if you get an unexpected error force it into the same format but supply an error message
    handleOrderObjectError(error) {
        if (error && error.hasOwnProperty('message')) {
            // Server sent us back an object with obj.message = 'xxxx'
            // so pass that to angular
            return of(error);
        } else if (error) {
            // Unknown error, structure it in a way our angular code can handle
            return of({ isSuccess: false, message: 'error: ' + (error.toString()), data: null } as IApiTypedResult<Order>);
        } else {
            // Unknown error, structure it in a way our angular code can handle
            return of({ isSuccess: false, message: 'unknown error', data: null } as IApiTypedResult<Order>);
        }
    }


    // High level error handler for angular components, show message, clean up page.
    // errorObject is anything that has a .message property (true angular error or ApiResult) or null.
    // If no message found, uses default or 'Unknown error'.  If stayHere will remain, else go to order tracker.
    // Error message displayed as toaster.
    handleOrderError(myComponent: any, errorObject: any, defaultMessage: string = null, stayHere: boolean = false) {
        myComponent.pageState = this.PAGE_STATE_READY; // stop spinner
        let message = errorObject ? errorObject : 'It looks like something went wrong. Try reloading the page or contacting support if this issue continues.';

        if (errorObject) {
            if (errorObject.hasOwnProperty('message') && errorObject.message) {
                message = errorObject.message;
            }
        }
        this.toastr.error(message);
        if (!stayHere) {
            myComponent.router.navigate(['/order-tracker']);
        }
    }

    // Delivery priority descriptions
    getDeliveryPriorityDescription(deliverPriority: string) {
        let result = '';

        switch (deliverPriority) {
            case this.DELIVERY_PRIORITY_REGULAR:
                result = 'Regular';
                break;
            case this.DELIVERY_PRIORITY_EXPEDITED:
                result = 'Expedited';
                break;
            case this.DELIVERY_PRIORITY_EMERGENCY:
                result = 'Emergency';
                break;
        }
        return result;
    }

}
